import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useSecurityEvents, useUserDevices, useUserSessions, useBlockDevice, useTerminateSession } from '@/hooks/useSecurity';
import { Shield, Activity, AlertTriangle, Smartphone, LogOut, Ban } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { format } from 'date-fns';

export default function SecurityDashboard() {
  const { isAdmin, loading: authLoading } = useAuth();
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>();

  const { data: securityEvents } = useSecurityEvents(selectedUserId);
  const { data: devices, refetch: refetchDevices } = useUserDevices(selectedUserId);
  const { data: sessions, refetch: refetchSessions } = useUserSessions(selectedUserId);
  
  const blockDevice = useBlockDevice();
  const terminateSession = useTerminateSession();

  if (authLoading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  const handleBlockDevice = async (deviceId: string) => {
    try {
      await blockDevice.mutateAsync(deviceId);
      toast({ title: 'Device blocked successfully' });
      refetchDevices();
    } catch (error) {
      toast({ title: 'Failed to block device', variant: 'destructive' });
    }
  };

  const handleTerminateSession = async (sessionId: string) => {
    try {
      await terminateSession.mutateAsync(sessionId);
      toast({ title: 'Session terminated successfully' });
      refetchSessions();
    } catch (error) {
      toast({ title: 'Failed to terminate session', variant: 'destructive' });
    }
  };

  const severityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'destructive';
      case 'high': return 'destructive';
      case 'medium': return 'secondary';
      case 'low': return 'outline';
      default: return 'outline';
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1 container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto space-y-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-2">
                <Shield className="h-8 w-8" />
                Security Dashboard
              </h1>
              <p className="text-muted-foreground mt-2">
                Monitor account security, device tracking, and suspicious activity
              </p>
            </div>
          </div>

          {/* Security Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Events</CardTitle>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{securityEvents?.length || 0}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Critical Events</CardTitle>
                <AlertTriangle className="h-4 w-4 text-destructive" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {securityEvents?.filter(e => e.severity === 'critical').length || 0}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Active Devices</CardTitle>
                <Smartphone className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {devices?.filter(d => !d.is_blocked).length || 0}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Active Sessions</CardTitle>
                <LogOut className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {sessions?.filter(s => s.is_active).length || 0}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Security Tabs */}
          <Tabs defaultValue="events" className="w-full">
            <TabsList>
              <TabsTrigger value="events">Security Events</TabsTrigger>
              <TabsTrigger value="devices">Devices</TabsTrigger>
              <TabsTrigger value="sessions">Sessions</TabsTrigger>
            </TabsList>

            <TabsContent value="events" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Recent Security Events</CardTitle>
                  <CardDescription>
                    Monitoring suspicious activity, rate limits, and security violations
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {securityEvents?.map((event) => (
                      <div
                        key={event.id}
                        className="flex items-start justify-between p-4 border rounded-lg"
                      >
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <Badge variant={severityColor(event.severity)}>
                              {event.severity}
                            </Badge>
                            <span className="font-medium">{event.event_type}</span>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            IP: {event.ip_address || 'Unknown'} • {format(new Date(event.created_at), 'PPp')}
                          </p>
                          {event.details && (
                            <pre className="text-xs text-muted-foreground mt-2">
                              {JSON.stringify(event.details, null, 2)}
                            </pre>
                          )}
                        </div>
                      </div>
                    ))}
                    {!securityEvents?.length && (
                      <p className="text-center text-muted-foreground py-8">
                        No security events recorded
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="devices" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Registered Devices</CardTitle>
                  <CardDescription>
                    Maximum 3 devices allowed per account
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {devices?.map((device) => (
                      <div
                        key={device.id}
                        className="flex items-start justify-between p-4 border rounded-lg"
                      >
                        <div className="space-y-1 flex-1">
                          <div className="flex items-center gap-2">
                            <Smartphone className="h-4 w-4" />
                            <span className="font-medium">{device.device_name}</span>
                            {device.is_blocked && <Badge variant="destructive">Blocked</Badge>}
                            {device.is_trusted && <Badge variant="outline">Trusted</Badge>}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Last IP: {device.last_ip_address}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Last seen: {format(new Date(device.last_seen_at), 'PPp')}
                          </p>
                        </div>
                        {!device.is_blocked && (
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleBlockDevice(device.id)}
                          >
                            <Ban className="h-4 w-4 mr-2" />
                            Block
                          </Button>
                        )}
                      </div>
                    ))}
                    {!devices?.length && (
                      <p className="text-center text-muted-foreground py-8">
                        No devices registered
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="sessions" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Active Sessions</CardTitle>
                  <CardDescription>
                    Only 1 concurrent session allowed per account
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {sessions?.map((session) => (
                      <div
                        key={session.id}
                        className="flex items-start justify-between p-4 border rounded-lg"
                      >
                        <div className="space-y-1 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">Session {session.id.slice(0, 8)}</span>
                            {session.is_active ? (
                              <Badge variant="default">Active</Badge>
                            ) : (
                              <Badge variant="outline">Inactive</Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            IP: {session.ip_address}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Started: {format(new Date(session.started_at), 'PPp')}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Last activity: {format(new Date(session.last_activity_at), 'PPp')}
                          </p>
                        </div>
                        {session.is_active && (
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleTerminateSession(session.id)}
                          >
                            <LogOut className="h-4 w-4 mr-2" />
                            Terminate
                          </Button>
                        )}
                      </div>
                    ))}
                    {!sessions?.length && (
                      <p className="text-center text-muted-foreground py-8">
                        No sessions found
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>
      <Footer />
    </div>
  );
}
